import { supabase } from '../lib/supabase';
import type { CwiCohort, CwiOverview, CwiParticipant, CwiProgram, CwiWorkspaceData, OrganizationBranding } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */
const programFromRow = (row: any): CwiProgram => ({
  id: row.id,
  name: row.name,
  description: row.description ?? '',
  status: row.status,
  startsAt: row.starts_at,
  endsAt: row.ends_at,
  grantsPlan: row.grants_plan,
  curriculumKey: row.curriculum_key ?? undefined,
});

const cohortFromRow = (row: any): CwiCohort => ({
  id: row.id,
  name: row.name,
  programName: row.program_name,
  status: row.status,
  code: row.code,
  seats: Number(row.seats),
  startsAt: row.starts_at,
  endsAt: row.ends_at ?? undefined,
  facilitatorUserId: row.facilitator_user_id ?? undefined,
  enrolled: Number(row.enrolled),
  active: Number(row.active),
  completed: Number(row.completed),
  averageProgress: row.average_progress == null ? null : Number(row.average_progress),
  suppressed: Boolean(row.suppressed),
  minGroupSize: Number(row.min_group_size),
});

const participantFromRow = (row: any): CwiParticipant => ({
  userId: row.user_id,
  email: row.email,
  state: row.state,
  joinedAt: row.joined_at,
  lastActiveAt: row.last_active_at,
  courseProgress: Number(row.course_progress),
  lessonsCompleted: Number(row.lessons_completed),
  quizzesPassed: Number(row.quizzes_passed),
  baselineComplete: Boolean(row.baseline_complete),
  endlineComplete: Boolean(row.endline_complete),
  certificateEligible: Boolean(row.certificate_eligible),
  attendancePresent: Number(row.attendance_present),
});
/* eslint-enable @typescript-eslint/no-explicit-any */

export async function loadCwiWorkspace(organizationId: string): Promise<CwiWorkspaceData> {
  const [overviewResult, programResult, cohortResult, brandingResult] = await Promise.all([
    supabase.rpc('partner_os_cwi_overview', { target_org: organizationId }),
    supabase.from('programs').select('*').eq('organization_id', organizationId).order('starts_at', { ascending: false }),
    supabase.rpc('partner_os_cwi_cohorts', { target_org: organizationId }),
    supabase.from('organization_branding').select('*').eq('organization_id', organizationId).maybeSingle(),
  ]);
  for (const result of [overviewResult, programResult, cohortResult, brandingResult]) {
    if (result.error) throw new Error(result.error.message);
  }
  const brandingRow = brandingResult.data;
  const branding: OrganizationBranding | null = brandingRow ? {
    organizationId: brandingRow.organization_id,
    displayName: brandingRow.display_name,
    logoUrl: brandingRow.logo_url ?? undefined,
    primaryColor: brandingRow.primary_color ?? undefined,
  } : null;
  return {
    overview: overviewResult.data as CwiOverview,
    programs: (programResult.data ?? []).map(programFromRow),
    cohorts: (cohortResult.data ?? []).map(cohortFromRow),
    branding,
  };
}

export async function loadCwiRoster(cohortId: string): Promise<CwiParticipant[]> {
  const { data, error } = await supabase.rpc('partner_os_cwi_roster', { target_cohort: cohortId });
  if (error) throw new Error(error.message);
  return (data ?? []).map(participantFromRow);
}

export async function createCwiInvitation(cohortId: string, email: string) {
  const { data, error } = await supabase.rpc('partner_os_create_cwi_invitation', {
    target_cohort: cohortId,
    target_email: email,
  });
  if (error) throw new Error(error.message);
  return Array.isArray(data) ? data[0] : data;
}

export async function createCwiCohort(input: {
  organizationId: string;
  programId: string;
  name: string;
  code: string;
  seats: number;
  startsAt: string;
  endsAt: string;
}) {
  const { data, error } = await supabase.from('cohorts').insert({
    org_id: input.organizationId,
    program_id: input.programId,
    name: input.name,
    code: input.code.trim().toUpperCase(),
    status: 'enrolling',
    seats: input.seats,
    starts_at: input.startsAt,
    ends_at: input.endsAt,
    grants_plan: 'builder',
    program: 'cwi',
    course_id: 'cwi',
  }).select('id').single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateOrganizationBranding(branding: OrganizationBranding) {
  const { error } = await supabase.from('organization_branding').upsert({
    organization_id: branding.organizationId,
    display_name: branding.displayName,
    logo_url: branding.logoUrl ?? null,
    primary_color: branding.primaryColor ?? null,
  }, { onConflict: 'organization_id' });
  if (error) throw new Error(error.message);
}
